import { relations, sql } from 'drizzle-orm'
import { boolean, index, integer, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'
import z from 'zod/v4'

import { organization } from '../../auth'
import { Invoice } from '../invoice'
import { Premium } from '../premium'
import { MembershipAddOn } from './add-on'

export const Membership = pgTable(
  'membership',
  {
    id: uuid().primaryKey().defaultRandom(),
    invoiceId: uuid().references(() => Invoice.id, { onDelete: 'restrict' }),
    organizationId: text()
      .notNull()
      .references(() => organization.id, { onDelete: 'cascade' }),
    seats: integer().notNull(),
    startDate: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    needToShowBannerExpired: boolean().notNull().default(false),
    expiredNotice: boolean().notNull().default(false),
    endDate: timestamp({ mode: 'date', withTimezone: true }).notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    // Index for organization lookups
    index('idx_membership_organization').on(t.organizationId),
    // Composite for DISTINCT ON (organization_id) ORDER BY organization_id, end_date DESC (e.g. reminder-expired-membership cron)
    index('idx_membership_organization_end_date').on(t.organizationId, t.endDate.desc()),
  ],
)

export const membershipRelations = relations(Membership, ({ one, many }) => ({
  organization: one(organization, {
    fields: [Membership.organizationId],
    references: [organization.id],
  }),

  invoice: one(Invoice, {
    fields: [Membership.invoiceId],
    references: [Invoice.id],
  }),

  addOns: many(MembershipAddOn),

  premiums: many(Premium),
}))

export const InsertMembershipSchema = createInsertSchema(Membership, {
  invoiceId: z.uuid().optional(),
  organizationId: z.string(),
  seats: z.number().int().positive(),
  startDate: z.date(),
  needToShowBannerExpired: z.boolean().default(false),
  expiredNotice: z.boolean().default(false),
  endDate: z.date(),
})
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
  })
  .superRefine((data, ctx) => {
    // Validate that endDate is after startDate
    if (data.endDate <= data.startDate) {
      ctx.addIssue({
        code: 'custom',
        message: 'End date must be after start date',
        path: ['endDate'],
      })
    }
  })

export const UpdateMembershipSchema = createUpdateSchema(Membership, {
  needToShowBannerExpired: z.boolean(),
  expiredNotice: z.boolean(),
})
  .omit({
    id: true,
    invoiceId: true,
    organizationId: true,
    seats: true,
    startDate: true,
    endDate: true,
    createdAt: true,
    updatedAt: true,
  })
  .partial()

export const SelectMembershipSchema = createSelectSchema(Membership, {
  id: z.uuid(),
  invoiceId: z.uuid().nullable(),
  organizationId: z.string(),
  seats: z.number().int(),
  startDate: z.date(),
  needToShowBannerExpired: z.boolean(),
  expiredNotice: z.boolean(),
  endDate: z.date(),
  createdAt: z.date(),
  updatedAt: z.date().nullable(),
})

export type InsertMembership = typeof Membership.$inferInsert
export type UpdateMembership = z.infer<typeof UpdateMembershipSchema>
export type SelectMembership = typeof Membership.$inferSelect
